/**
 * Shared building blocks for the future-module PREVIEW pages
 * (`/preview/discord`, `/preview/inbox`).
 *
 * These pages are deliberately NON-FUNCTIONAL mockups — every number on them is
 * hardcoded, representative sample data, not a live read. The portal carries a
 * strict honest-labelling discipline (see PortalHome / the salience module), so
 * every preview surface MUST wear the PreviewBanner: it states plainly that the
 * module isn't built yet and the figures are illustrative. The point is to make
 * the roadmap tangible in a pitch, not to imply a capability that ships today.
 */
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

/** Full-width "this is a concept, not live" banner pinned to the top of every
 * preview page. Amber (agenda-elite) so it reads as a flag, not a live signal. */
export function PreviewBanner({ module }: { module: string }) {
  return (
    <div className="mb-6 flex flex-col gap-2 rounded-xl border border-agenda-elite/40 bg-agenda-elite/10 p-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-center gap-2.5">
        <span className="shrink-0 rounded bg-agenda-elite px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-terminal">
          Preview
        </span>
        <p className="text-[13px] leading-snug text-ink">
          <span className="font-semibold">{module}</span> is a roadmap concept —
          not yet built. Every figure below is illustrative sample data to show
          what the module could look like.
        </p>
      </div>
      <Link
        to="/"
        className="inline-flex shrink-0 items-center gap-1.5 self-start rounded border border-rule bg-surface px-2.5 py-1 text-[12px] font-semibold text-ink-muted transition-colors hover:text-ink sm:self-auto"
      >
        <ArrowLeft size={13} /> Back to portal
      </Link>
    </div>
  );
}

/** Page header in the same register as PortalHome / the module pages. */
export function PreviewHeader({
  kicker,
  title,
  blurb,
}: {
  kicker: string;
  title: string;
  blurb: string;
}) {
  return (
    <header className="mb-6">
      <div className="text-xs font-semibold uppercase tracking-wider text-ink-muted">
        {kicker}
      </div>
      <h1 className="mt-1 text-2xl font-bold tracking-tight text-ink">{title}</h1>
      <p className="mt-1.5 max-w-2xl text-sm text-ink-muted">{blurb}</p>
    </header>
  );
}

/** A terminal-register stat tile (mirrors PortalHome's PulseTile, minus links). */
export function StatTile({
  kicker,
  value,
  label,
  sub,
  accent,
}: {
  kicker: string;
  value: string;
  label: string;
  sub?: string;
  accent?: "up" | "down";
}) {
  return (
    <div className="flex min-w-0 flex-col rounded-xl border border-white/10 bg-terminal-panel p-4">
      <div className="mb-2 truncate text-[10px] font-semibold uppercase tracking-wider text-terminal-ink-muted">
        {kicker}
      </div>
      <div
        className={`font-mono text-2xl font-bold leading-none tabular-nums ${
          accent === "up"
            ? "text-up"
            : accent === "down"
              ? "text-down"
              : "text-terminal-ink"
        }`}
      >
        {value}
      </div>
      <div className="mt-1.5 text-[11px] uppercase tracking-wider text-terminal-ink-muted">
        {label}
      </div>
      {sub && (
        <div className="mt-1 text-[11px] text-terminal-ink-muted/80">{sub}</div>
      )}
    </div>
  );
}

/** A labelled horizontal bar (share-of-total) for breakdown lists. */
export function BarRow({
  label,
  value,
  max,
  suffix,
  colour = "var(--color-agenda-public)",
}: {
  label: string;
  value: number;
  max: number;
  suffix?: string;
  colour?: string;
}) {
  const pct = max > 0 ? Math.round((100 * value) / max) : 0;
  return (
    <div className="flex items-center gap-3">
      <span className="w-32 shrink-0 truncate text-[13px] text-terminal-ink">
        {label}
      </span>
      <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-white/5">
        <div
          className="h-full rounded-full"
          style={{ width: `${pct}%`, background: colour }}
        />
      </div>
      <span className="w-14 shrink-0 text-right font-mono text-xs tabular-nums text-terminal-ink-muted">
        {value.toLocaleString("en-GB")}
        {suffix}
      </span>
    </div>
  );
}

/** A coloured category/priority pill. */
export function Pill({
  children,
  tone = "neutral",
}: {
  children: ReactNode;
  tone?: "press" | "business" | "fan" | "spam" | "high" | "med" | "low" | "neutral";
}) {
  const cls: Record<string, string> = {
    press: "bg-agenda-media/15 text-agenda-media",
    business: "bg-agenda-elite/15 text-agenda-elite",
    fan: "bg-up/15 text-up",
    spam: "bg-down/15 text-down",
    high: "bg-down/15 text-down",
    med: "bg-agenda-elite/15 text-agenda-elite",
    low: "bg-white/10 text-terminal-ink-muted",
    neutral: "bg-white/10 text-terminal-ink-muted",
  };
  return (
    <span
      className={`inline-block rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${cls[tone]}`}
    >
      {children}
    </span>
  );
}
